import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../lib/apiUrl';

const isValidEmail = (email) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const EmailAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState('email'); // 'email' | 'sign_in' | 'sign_up' | 'set_password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Signup-only fields
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('https://');
  const [teamSize, setTeamSize] = useState('1-10');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.state?.email) setEmail(location.state.email);
  }, [location.state]);

  // ----- Email entry: detect which flow to show -----
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/check-account`, {
        email: email.trim().toLowerCase(),
      });
      setStep(data.status); // 'sign_in' | 'sign_up' | 'set_password'
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Unable to verify email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ----- Sign in -----
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signin`, {
        email: email.trim().toLowerCase(),
        password,
      });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || data.user.email}!`);
      navigate('/dashboard');
    } catch (err) {
      // 409 from backend means "no password set yet" → switch to set_password
      if (err.response?.status === 409) {
        setStep('set_password');
        setError('');
        toast.info('This account needs a password. Please create one.');
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ----- Sign up -----
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Please enter your name');
    if (password.length < 8) return setError('Password must be at least 8 characters long');
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
      return setError('Password must contain at least one letter and one number');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (!companyName.trim()) return setError('Please enter your company name');
    if (!website.startsWith('https://') || website === 'https://')
      return setError('Please enter a valid company website starting with https://');

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        company_name: companyName.trim(),
        website: website.trim(),
        team_size: teamSize,
      });
      login(data.token, data.user);
      toast.success('Account created! Welcome to AIVORA Comply360.');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ----- Set initial password (legacy OTP-only user) -----
  const handleSetInitialPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters long');
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
      return setError('Password must contain at least one letter and one number');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/set-initial-password`, {
        email: email.trim().toLowerCase(),
        password,
      });
      login(data.token, data.user);
      toast.success('Password set! You are now signed in.');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(52,211,153,0.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(99,102,241,0.06), transparent 60%), linear-gradient(180deg, #060914 0%, #0a0f1f 100%)',
      }}
    >
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        <div
          className="rounded-2xl p-8 md:p-10"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.5), 0 0 80px -20px rgba(52,211,153,0.12)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-7">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 border"
              style={{ background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(52,211,153,0.3)' }}
            >
              <ShieldCheck className="h-6 w-6 text-emerald-300" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-white mb-2">
              {step === 'email' && 'Sign in to AIVORA Comply360'}
              {step === 'sign_in' && 'Welcome back'}
              {step === 'sign_up' && 'Create your account'}
              {step === 'set_password' && 'Set your password'}
            </h1>
            <p className="text-slate-400 text-sm">
              {step === 'email' && 'Enter your email to get started.'}
              {step === 'sign_in' && email}
              {step === 'sign_up' && 'Just a few quick details and you are in.'}
              {step === 'set_password' && 'Your account exists — choose a password to continue.'}
            </p>
          </div>

          {/* Error banner */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 px-4 py-3 rounded-lg text-sm text-red-300"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
                data-testid="auth-error"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ===== STEP: EMAIL ===== */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                  Work Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                    placeholder="you@company.com"
                    data-testid="email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="email-continue-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
              </Button>
            </form>
          )}

          {/* ===== STEP: SIGN IN ===== */}
          {step === 'sign_in' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <PasswordField
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="Password"
                placeholder="Your password"
                autoFocus
                testid="signin-password-input"
              />
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password', { state: { email } })}
                  className="text-xs text-emerald-300 hover:text-emerald-200 transition-colors font-mono uppercase tracking-widest"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="signin-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
              <BackLink onClick={goBack} />
            </form>
          )}

          {/* ===== STEP: SIGN UP ===== */}
          {step === 'sign_up' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <SimpleField
                label="Full Name"
                value={name}
                onChange={setName}
                placeholder="Jane Doe"
                autoFocus
                testid="signup-name-input"
              />
              <PasswordField
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="Password"
                placeholder="Min 8 chars · letter + number"
                testid="signup-password-input"
              />
              <PasswordField
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="Confirm Password"
                placeholder="Re-enter password"
                testid="signup-confirm-password-input"
              />
              <SimpleField
                label="Company Name"
                value={companyName}
                onChange={setCompanyName}
                placeholder="Acme Corp"
                testid="signup-company-input"
              />
              <SimpleField
                label="Company Website"
                value={website}
                onChange={setWebsite}
                placeholder="https://acme.com"
                testid="signup-website-input"
              />
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                  Team Size
                </label>
                <select
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="w-full px-3 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
                  data-testid="signup-team-size-select"
                >
                  {['1-10', '11-50', '51-200', '201-1000', '1000+'].map((s) => (
                    <option key={s} value={s} className="bg-slate-900">
                      {s} people
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="signup-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
              </Button>
              <p className="text-center text-[11px] leading-5 text-slate-500" data-testid="signup-privacy-notice">
                By creating an account, you acknowledge our <a href="/privacy" className="text-emerald-300 hover:text-emerald-200" data-testid="signup-privacy-link">Privacy Policy</a>.
              </p>
              <BackLink onClick={goBack} />
            </form>
          )}

          {/* ===== STEP: SET INITIAL PASSWORD ===== */}
          {step === 'set_password' && (
            <form onSubmit={handleSetInitialPassword} className="space-y-4">
              <div className="px-3 py-2.5 rounded-lg text-xs text-emerald-300 bg-emerald-400/8 border border-emerald-400/20">
                Your account exists from a previous login. Set a password now and you won't need OTPs anymore.
              </div>
              <PasswordField
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="New Password"
                placeholder="Min 8 chars · letter + number"
                autoFocus
                testid="set-password-input"
              />
              <PasswordField
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="Confirm Password"
                placeholder="Re-enter password"
                testid="set-confirm-password-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="set-password-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Password & Sign In'}
              </Button>
              <BackLink onClick={goBack} />
            </form>
          )}

          {/* Footer */}
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 text-center mt-6">
            🔐 Protected by bcrypt · 256-bit JWT
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// ===== Helper components =====

const SimpleField = ({ label, value, onChange, placeholder, autoFocus, testid }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required
      autoFocus={autoFocus}
      className="w-full px-3 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
      data-testid={testid}
    />
  </div>
);

const PasswordField = ({ value, onChange, show, onToggleShow, label, placeholder, autoFocus, testid }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
      {label}
    </label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoFocus={autoFocus}
        className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
        placeholder={placeholder}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
        aria-label="Toggle password visibility"
        data-testid={`${testid}-toggle`}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

const BackLink = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors font-mono uppercase tracking-widest pt-2"
    data-testid="auth-back-link"
  >
    <ArrowLeft className="h-3 w-3" /> Use a different email
  </button>
);

export default EmailAuth;
